// Fix GX (2026-10-19): pantalla de marcación del personal (celular). Ruta pública /marcar.
// El trabajador entra con su cédula y su PIN (o lo hace desde el celular de un compañero), toca el botón
// que le toca (entrada, salida a almuerzo, vuelta del almuerzo, salida) y el servidor valida que esté dentro
// del radio de su obra. La ubicación se toma solo en el momento de marcar: no hay seguimiento.
// Usa fetch (no axios) a propósito: esta sesión no es la del ERP y no debe pasar por su renovación de sesión.
import { useState, useEffect, useCallback, useRef } from "react";
import { MapPin, Loader2, LogOut, Check, AlertTriangle, ShieldCheck, WifiOff, Navigation } from "lucide-react";
import { API } from "../contexts/AuthContext";

const K_SESION = "cen_marcacion_sesion";
const K_CI = "cen_marcacion_ci";
const ORDEN = [["entrada", "Entrada"], ["almuerzo_salida", "Salida a almuerzo"], ["almuerzo_vuelta", "Vuelta del almuerzo"], ["salida", "Salida"]];
const COLOR = { entrada: "bg-emerald-600 hover:bg-emerald-700", almuerzo_salida: "bg-amber-500 hover:bg-amber-600", almuerzo_vuelta: "bg-sky-600 hover:bg-sky-700", salida: "bg-[#2E6DA4] hover:bg-[#245A8A]" };

const leer = (store, k) => { try { return JSON.parse(store.getItem(k)); } catch { return null; } };
const guardar = (store, k, v) => { try { store.setItem(k, JSON.stringify(v)); } catch { /* sin almacenamiento: la sesión dura lo que dure la pantalla */ } };
const olvidarSesion = () => { try { sessionStorage.removeItem(K_SESION); localStorage.removeItem(K_SESION); } catch { /* nada que borrar */ } };
const leerSesion = () => leer(sessionStorage, K_SESION) || leer(localStorage, K_SESION);
// Fix IT (2026-11-02): "marcar sin internet, con la hora y el GPS, y subirlo al volver la señal" (reunión CEN, 30/10).
// Sin señal, la marca (tipo, ubicación y hora del celular corregida con la del servidor) queda en el celular y se sube
// sola cuando vuelve la conexión, con la sesión de esa misma persona. El servidor la valida igual que una normal.
const K_COLA = "cen_marcacion_cola";
const K_HOY = "cen_marcacion_hoy";
const leerCola = () => { const c = leer(localStorage, K_COLA); return Array.isArray(c) ? c : []; };
const widDe = (token) => { try { return JSON.parse(atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).wid || ""; } catch { return ""; } };
const fechaPy = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/Asuncion" });
const horaPy = (d) => d.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit", timeZone: "America/Asuncion", hour12: false });
const etiquetaDe = (tipo) => ORDEN.find(([t]) => t === tipo)?.[1] || tipo;
const siguientesDe = (hechos) => (hechos.has("salida") ? [] : !hechos.has("entrada") ? ["entrada"]
  : !hechos.has("almuerzo_salida") ? ["almuerzo_salida", "salida"] : !hechos.has("almuerzo_vuelta") ? ["almuerzo_vuelta"] : ["salida"]);

class ErrorApi extends Error {
  constructor(mensaje, status, detalle) { super(mensaje); this.status = status; this.detalle = detalle; }
}
async function api(ruta, { method = "GET", body, token } = {}) {
  let r;
  try {
    r = await fetch(`${API}/marcacion${ruta}`, {
      method, credentials: "omit",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ErrorApi("No hay conexión. Revisá tu señal e intentá de nuevo.", 0, null);
  }
  let data = null;
  try { data = await r.json(); } catch { /* sin cuerpo */ }
  if (!r.ok) {
    const d = data?.detail;
    throw new ErrorApi(typeof d === "string" ? d : d?.mensaje || "No se pudo completar. Intentá de nuevo.", r.status, typeof d === "object" ? d : null);
  }
  return data;
}

// Toma la mejor ubicación que dé el celular en unos segundos (se queda con la más precisa).
function obtenerUbicacion({ maxMs = 12000, objetivoM = 25 } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject({ code: 2 }); return; }
    let mejor = null, fin = false, id = null, t = null;
    const cerrar = (f) => { if (fin) return; fin = true; if (id != null) navigator.geolocation.clearWatch(id); clearTimeout(t); f(); };
    id = navigator.geolocation.watchPosition(
      (p) => { if (!mejor || p.coords.accuracy < mejor.coords.accuracy) mejor = p; if (p.coords.accuracy <= objetivoM) cerrar(() => resolve(mejor)); },
      (e) => { if (!mejor) cerrar(() => reject(e)); },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
    t = setTimeout(() => cerrar(() => (mejor ? resolve(mejor) : reject({ code: 3 }))), maxMs);
  });
}

function Marco({ children }) {
  const [enLinea, setEnLinea] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const si = () => setEnLinea(true), no = () => setEnLinea(false);
    window.addEventListener("online", si); window.addEventListener("offline", no);
    return () => { window.removeEventListener("online", si); window.removeEventListener("offline", no); };
  }, []);
  useEffect(() => { const t = document.title; document.title = "Marcación — CEN Ingeniería"; return () => { document.title = t; }; }, []);
  return (
    <div className="min-h-screen bg-[#F4F7FB] flex justify-center" data-testid="marcar-page">
      <div className="w-full max-w-md px-4 py-6 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <img src="/logo_cen_transparent.png" alt="CEN" className="h-10 w-auto" />
          <div><p className="text-base font-bold text-gray-900 leading-tight">Marcación de asistencia</p><p className="text-xs text-gray-500">CEN Ingeniería</p></div>
        </div>
        {!enLinea && <p className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="marcar-sin-conexion"><WifiOff size={16} className="shrink-0" />Sin conexión. Con tu sesión abierta podés marcar igual: la marca se guarda en el celular con la hora y la ubicación, y se sube sola cuando vuelva la señal.</p>}
        {children}
        <p className="text-[11px] text-gray-400 text-center mt-2">Usamos tu ubicación solo en el momento de marcar, para confirmar que estás en la obra. No te seguimos durante el día.</p>
      </div>
    </div>
  );
}

function Ingreso({ onEntro, aviso }) {
  const [ci, setCi] = useState(() => leer(localStorage, K_CI) || "");
  const [pin, setPin] = useState("");
  const [mio, setMio] = useState(() => !!leer(localStorage, K_CI));
  const [error, setError] = useState(aviso || "");
  const [cargando, setCargando] = useState(false);
  const guardadas = leerCola().length;   // Fix IT

  const entrar = async (e) => {
    e.preventDefault();
    if (!ci.trim() || pin.length < 4) { setError("Escribí tu cédula y tu PIN."); return; }
    setCargando(true); setError("");
    try {
      const d = await api("/login", { method: "POST", body: { cedula: ci, pin } });
      const sesion = { token: d.token, nombre: d.trabajador.nombre, mio };
      guardar(mio ? localStorage : sessionStorage, K_SESION, sesion);
      if (mio) guardar(localStorage, K_CI, ci); else { try { localStorage.removeItem(K_CI); } catch { /* sin almacenamiento */ } }
      onEntro(sesion);
    } catch (err) { setError(err.message); setPin(""); }
    finally { setCargando(false); }
  };

  return (
    <form onSubmit={entrar} className="bg-white rounded-2xl shadow-sm border border-gray-200/70 p-5 space-y-4" data-testid="marcar-ingreso">
      <div><h1 className="text-lg font-bold text-gray-900">Ingresá para marcar</h1><p className="text-sm text-gray-500">Con tu cédula y el PIN que te dio RRHH. Si no tenés celular, podés ingresar desde el de un compañero.</p></div>
      <div><label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Cédula</label>
        <input value={ci} onChange={(e) => setCi(e.target.value)} inputMode="numeric" autoComplete="off" placeholder="Ej: 1234567"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 text-lg focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="marcar-cedula" /></div>
      <div><label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">PIN</label>
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 text-lg tracking-[0.4em] focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="marcar-pin" /></div>
      <label className="flex items-start gap-3 text-sm text-gray-600"><input type="checkbox" checked={mio} onChange={(e) => setMio(e.target.checked)} className="mt-0.5 h-5 w-5" data-testid="marcar-mi-celular" />
        <span>Es mi celular: recordar mi cédula y no cerrar la sesión después de marcar.</span></label>
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="marcar-error">{error}</p>}
      {guardadas > 0 && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="marcar-cola-ingreso">Hay {guardadas} marca{guardadas !== 1 ? "s" : ""} guardada{guardadas !== 1 ? "s" : ""} en este celular sin subir. Ingresá con tu cédula y se suben solas.</p>}
      <button type="submit" disabled={cargando} className="w-full flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white font-bold rounded-xl py-4 text-base disabled:opacity-60" data-testid="marcar-ingresar">
        {cargando ? <Loader2 size={20} className="animate-spin" /> : <ShieldCheck size={20} />}Ingresar</button>
    </form>
  );
}

function Inicio({ sesion, onSalir }) {
  const [hoy, setHoy] = useState(null);
  const [error, setError] = useState("");
  const [pendiente, setPendiente] = useState(null);       // tipo esperando confirmación
  const [paso, setPaso] = useState("");                    // "ubicacion" | "enviando" | ""
  const [resultado, setResultado] = useState(null);        // { ok, mensaje }
  const [cuenta, setCuenta] = useState(null);              // segundos para cerrar la sesión
  const [offset, setOffset] = useState(0);
  const [, tic] = useState(0);
  const resRef = useRef(null);
  const token = sesion.token;
  const wid = widDe(token);   // Fix IT
  const [cola, setCola] = useState(() => leerCola().filter((c) => c.wid === wid));
  const [rechazos, setRechazos] = useState([]);
  const subiendo = useRef(false);

  const cargar = useCallback(async () => {
    try {
      const d = await api("/hoy", { token });
      const off = Date.parse(d.ahora_iso) - Date.now();
      setHoy(d); setOffset(off); setError("");
      if (wid) guardar(localStorage, K_HOY, { wid, d, off });   // Fix IT: para poder marcar si se abre sin señal
    } catch (e) {
      if (e.status === 401 || e.status === 403) { onSalir(e.message); return; }
      const c = e.status === 0 ? leer(localStorage, K_HOY) : null;   // Fix IT: sin señal, lo último que se vio
      if (c && wid && c.wid === wid && c.d) { setHoy((prev) => prev || c.d); setOffset((o) => o || c.off || 0); setError(""); }
      else setError(e.message);
    }
  }, [token, onSalir, wid]);
  useEffect(() => { cargar(); }, [cargar]);
  // Fix IT: sube en orden las marcas guardadas sin señal. Si sigue sin señal o la sesión venció, quedan guardadas;
  // si el servidor las rechaza (fuera del radio, día cerrado por RRHH...), salen de la cola y se avisa el motivo.
  const subirCola = useCallback(async () => {
    if (subiendo.current || !wid) return false;
    subiendo.current = true;
    let subio = false;
    try {
      for (const item of leerCola().filter((c) => c.wid === wid)) {
        const quitar = () => guardar(localStorage, K_COLA, leerCola().filter((c) => c.id !== item.id));
        try {
          await api("/marcar", { method: "POST", token, body: { tipo: item.tipo, lat: item.lat, lng: item.lng, precision: item.precision,
            sin_senal: true, hora_dispositivo: item.hora_dispositivo } });
          quitar(); subio = true;
        } catch (e) {
          if (e.status === 401) { onSalir(e.message); break; }   // vuelve a ingresar y se suben con la sesión nueva
          if (e.status === 0 || e.status >= 500) break;
          quitar();
          setRechazos((r) => [...r, `${etiquetaDe(item.tipo)} de las ${horaPy(new Date(item.hora_dispositivo))}: ${e.message}`]);
        }
      }
    } finally {
      subiendo.current = false;
      setCola(leerCola().filter((c) => c.wid === wid));
    }
    return subio;
  }, [token, wid, onSalir]);
  useEffect(() => {
    const intentar = async () => { if (leerCola().some((c) => c.wid === wid) && await subirCola()) await cargar(); };
    intentar();
    window.addEventListener("online", intentar);
    const id = setInterval(intentar, 60000);
    return () => { window.removeEventListener("online", intentar); clearInterval(id); };
  }, [subirCola, cargar, wid]);
  useEffect(() => { const id = setInterval(() => tic((n) => n + 1), 15000); return () => clearInterval(id); }, []);
  useEffect(() => {   // el resultado de la marca queda a la vista aunque el celular sea chico
    if (resultado && resRef.current) resRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [resultado]);
  useEffect(() => {   // sesión de celular compartido: se cierra sola después de marcar
    if (cuenta == null) return undefined;
    if (cuenta <= 0) { onSalir(); return undefined; }
    const id = setTimeout(() => setCuenta((c) => (c == null ? c : c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cuenta, onSalir]);

  const ahora = new Date(Date.now() + offset);
  const reloj = ahora.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit", timeZone: "America/Asuncion", hour12: false });
  const fechaTxt = ahora.toLocaleDateString("es-PY", { weekday: "long", day: "numeric", month: "long", timeZone: "America/Asuncion" });

  // Fix IT: guarda la marca en el celular para subirla cuando vuelva la señal.
  const guardarSinSenal = (tipo, pos) => {
    const momento = new Date(Date.now() + offset);
    const item = { id: `${momento.getTime()}-${Math.random().toString(36).slice(2, 7)}`, wid, tipo,
      lat: pos.coords.latitude, lng: pos.coords.longitude, precision: pos.coords.accuracy, hora_dispositivo: momento.toISOString() };
    guardar(localStorage, K_COLA, [...leerCola(), item]);
    setCola(leerCola().filter((c) => c.wid === wid));
    setResultado({ ok: true, mensaje: `Sin señal: tu ${etiquetaDe(tipo).toLowerCase()} de las ${horaPy(momento)} quedó guardada en el celular.`,
      detalle: "Se sube sola cuando vuelva la señal y cuenta con esa hora." });
    if (!sesion.mio) setCuenta(10);
  };

  const marcar = async (tipo) => {
    setPendiente(null); setResultado(null); setCuenta(null); setPaso("ubicacion");
    if (cola.length && navigator.onLine !== false) await subirCola();   // Fix IT: primero lo guardado, para respetar el orden
    let pos;
    try {
      pos = await obtenerUbicacion();
    } catch (e) {
      const motivo = e?.code === 1 ? "denegado" : e?.code === 3 ? "tiempo" : "no_disponible";
      api("/falla-gps", { method: "POST", token, body: { motivo } }).catch(() => {});
      setPaso("");
      setResultado({ ok: false, mensaje: motivo === "denegado"
        ? "No diste permiso de ubicación. Activala para este sitio en los ajustes del navegador y volvé a intentar. Se avisó a RRHH."
        : motivo === "tiempo" ? "El celular tardó demasiado en encontrar tu ubicación. Salí a cielo abierto y volvé a intentar. Se avisó a RRHH."
        : "No pudimos obtener tu ubicación. Activá el GPS y volvé a intentar. Se avisó a RRHH." });
      return;
    }
    if (navigator.onLine === false || leerCola().some((c) => c.wid === wid)) { guardarSinSenal(tipo, pos); setPaso(""); return; }   // Fix IT
    setPaso("enviando");
    try {
      const r = await api("/marcar", { method: "POST", token, body: { tipo, lat: pos.coords.latitude, lng: pos.coords.longitude, precision: pos.coords.accuracy } });
      setResultado({ ok: true, mensaje: r.mensaje, detalle: `Estabas a ${r.marca.distancia_m} m del centro de la obra (radio ${r.radio_m} m).` });
      await cargar();
      if (!sesion.mio) setCuenta(10);
    } catch (e) {
      if (e.status === 0) { guardarSinSenal(tipo, pos); return; }   // Fix IT: se cortó la señal al enviar
      if (e.status === 401) { onSalir(e.message); return; }
      setResultado({ ok: false, mensaje: e.message });
      await cargar();
    } finally { setPaso(""); }
  };

  // Fix IT: lo que se ve suma las marcas guardadas sin subir; si lo último que se vio es de otro día, arranca de cero.
  const fechaHoy = fechaPy(ahora);
  const baseHoy = hoy && (!hoy.fecha || hoy.fecha === fechaHoy) ? hoy
    : hoy ? { ...hoy, fecha: fechaHoy, marcas: [], siguientes: [{ tipo: "entrada", etiqueta: "Entrada" }], completa: false, aviso: null } : null;
  const colaHoy = cola.filter((c) => fechaPy(new Date(c.hora_dispositivo)) === fechaHoy);
  const marcasVista = [...(baseHoy?.marcas || []), ...colaHoy.map((c) => ({ tipo: c.tipo, hora: horaPy(new Date(c.hora_dispositivo)), sin_subir: true }))];
  const hechosVista = new Set(marcasVista.map((m) => m.tipo));
  const vista = baseHoy && { ...baseHoy, completa: baseHoy.completa || hechosVista.has("salida"),
    siguientes: colaHoy.length ? siguientesDe(hechosVista).map((t) => ({ tipo: t, etiqueta: etiquetaDe(t) })) : baseHoy.siguientes };
  const hechas = Object.fromEntries(marcasVista.map((m) => [m.tipo, m]));
  const sig = vista?.siguientes || [];

  return (
    <div className="space-y-4" data-testid="marcar-inicio">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/70 p-5">
        <div className="flex items-start justify-between gap-3">
          <div><p className="text-sm text-gray-500">Hola,</p><p className="text-xl font-bold text-gray-900 leading-tight" data-testid="marcar-nombre">{sesion.nombre}</p></div>
          <button onClick={() => onSalir()} className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 border border-gray-200 rounded-lg px-3 py-2" data-testid="marcar-salir"><LogOut size={15} />Salir</button>
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
          <p className="text-4xl font-bold tabular-nums text-gray-900" data-testid="marcar-reloj">{reloj}</p>
          <p className="text-sm text-gray-500 text-right first-letter:uppercase">{fechaTxt}</p>
        </div>
        {vista && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#EBF2F9] p-3 text-sm text-[#1f4f7a]">
            <MapPin size={17} className="mt-0.5 shrink-0" />
            <div><p className="font-semibold" data-testid="marcar-obra">{vista.obra.nombre || "Sin obra asignada"}</p>
              {vista.obra.radio_m && <p className="text-xs">Tenés que estar a menos de {vista.obra.radio_m} m de la obra para marcar.</p>}</div>
          </div>)}
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="marcar-error">{error}</p>}
      {!vista && !error && <p className="text-center text-gray-400 py-6"><Loader2 className="inline animate-spin" size={18} /> Cargando…</p>}
      {vista?.aviso && <p className="flex items-start gap-2 text-sm text-purple-900 bg-purple-50 border border-purple-100 rounded-xl p-3" data-testid="marcar-aviso"><AlertTriangle size={16} className="mt-0.5 shrink-0" />{vista.aviso}</p>}
      {cola.length > 0 && <p className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="marcar-cola"><WifiOff size={16} className="mt-0.5 shrink-0" />Tenés {cola.length} marca{cola.length !== 1 ? "s" : ""} guardada{cola.length !== 1 ? "s" : ""} sin subir. Se suben solas cuando vuelva la señal.</p>}
      {rechazos.length > 0 && (
        <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="marcar-rechazos">
          <p className="font-bold">No se pudieron registrar estas marcas guardadas:</p>
          <ul className="list-disc ml-5 mt-1">{rechazos.map((t, i) => <li key={i}>{t}</li>)}</ul>
          <button onClick={() => setRechazos([])} className="mt-2 font-bold underline">Entendido</button>
        </div>)}

      {vista && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/70 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Tus marcas de hoy</p>
          <div className="space-y-2.5" data-testid="marcar-marcas">
            {ORDEN.map(([tipo, etiqueta]) => {
              const m = hechas[tipo];
              const toca = sig.some((s) => s.tipo === tipo);
              return (
                <div key={tipo} className={`flex items-center justify-between rounded-xl px-3.5 py-3 ${m ? "bg-emerald-50" : toca ? "bg-sky-50 ring-1 ring-sky-200" : "bg-gray-50"}`} data-testid={`marca-${tipo}`}>
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">{m ? <Check size={16} className="text-emerald-600" /> : <span className="w-4" />}{etiqueta}</span>
                  <span className={`text-base font-bold tabular-nums text-right ${m ? "text-emerald-700" : "text-gray-300"}`}>{m ? m.hora : "—"}
                    {m?.sin_subir && <span className="block text-[10px] font-semibold text-amber-700" data-testid={`marca-sin-subir-${tipo}`}>sin subir</span>}
                    {m?.sin_senal && <span className="block text-[10px] font-semibold text-gray-500">sin señal</span>}</span>
                </div>);
            })}
          </div>
          {vista.completa && <p className="mt-4 text-sm font-semibold text-emerald-700 text-center" data-testid="marcar-completa">Listo, terminaste tu jornada de hoy. ¡Buen descanso!</p>}
        </div>)}

      {paso && (
        <div className="flex items-center gap-3 bg-white rounded-2xl border border-gray-200/70 p-5 text-gray-700" data-testid="marcar-paso">
          <Loader2 className="animate-spin text-[#2E6DA4]" size={22} />
          <p className="text-sm font-semibold">{paso === "ubicacion" ? "Buscando tu ubicación… no te muevas unos segundos." : "Registrando tu marca…"}</p>
        </div>)}

      {resultado && !paso && (
        <div ref={resRef} className={`rounded-2xl border p-5 ${resultado.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`} data-testid="marcar-resultado">
          <p className="flex items-start gap-2 font-bold">{resultado.ok ? <Check size={20} className="mt-0.5 shrink-0" /> : <AlertTriangle size={20} className="mt-0.5 shrink-0" />}{resultado.mensaje}</p>
          {resultado.detalle && <p className="text-sm mt-1 ml-7">{resultado.detalle}</p>}
          {cuenta != null && <p className="text-xs mt-3 ml-7">Cerramos tu sesión en {cuenta} s. <button onClick={() => setCuenta(null)} className="font-bold underline">Quedarme</button></p>}
          {!resultado.ok && <button onClick={() => setResultado(null)} className="mt-3 ml-7 text-sm font-bold underline">Entendido</button>}
        </div>)}

      {vista && !vista.aviso && !paso && sig.length > 0 && (
        <div className="space-y-3">
          {sig.map((s, i) => (
            <button key={s.tipo} onClick={() => setPendiente(s.tipo)} data-testid={`marcar-accion-${s.tipo}`}
              className={i === 0 ? `w-full flex items-center justify-center gap-2 text-white font-bold rounded-2xl py-5 text-lg shadow-sm active:scale-[0.99] ${COLOR[s.tipo]}`
                : "w-full flex items-center justify-center gap-2 font-bold rounded-2xl py-4 text-base border-2 border-gray-300 text-gray-700 bg-white active:scale-[0.99]"}>
              <Navigation size={20} />Marcar {s.etiqueta.toLowerCase()}</button>))}
        </div>)}

      {pendiente && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" data-testid="marcar-confirmacion">
          <div className="w-full max-w-md bg-white rounded-2xl p-5 space-y-4 shadow-2xl">
            <p className="text-lg font-bold text-gray-900">¿Marcar {ORDEN.find(([t]) => t === pendiente)?.[1].toLowerCase()} ahora?</p>
            <p className="text-sm text-gray-500">Son las {reloj}. Vamos a usar la ubicación de tu celular para confirmar que estás en la obra. Una vez marcada, no se puede deshacer.</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setPendiente(null)} className="py-3.5 rounded-xl border-2 border-gray-300 font-bold text-gray-700" data-testid="marcar-cancelar">Cancelar</button>
              <button onClick={() => marcar(pendiente)} className="py-3.5 rounded-xl bg-[#2E6DA4] text-white font-bold" data-testid="marcar-confirmar">Sí, marcar</button>
            </div>
          </div>
        </div>)}
    </div>
  );
}

export default function Marcar() {
  const [sesion, setSesion] = useState(leerSesion);
  const [aviso, setAviso] = useState("");
  const salir = useCallback((mensaje) => { olvidarSesion(); setSesion(null); setAviso(typeof mensaje === "string" ? mensaje : ""); }, []);
  return (
    <Marco>
      {sesion ? <Inicio key={sesion.token} sesion={sesion} onSalir={salir} /> : <Ingreso aviso={aviso} onEntro={(s) => { setAviso(""); setSesion(s); }} />}
    </Marco>
  );
}
